import { useEffect, useState } from 'react'
import {
  listAccounts, listGateways, registerUrls, stkPush,
  submitB2C, submitPochi, submitB2B, dispatchPayouts, simulateC2B,
  type Account, type Gateway,
} from '../api/monitoring'
import { fmtDateTime } from '../lib/format'
import { StatusChip } from '../components/StatusChip'
import { Can } from '../rbac/Can'
import { DrawerShell } from '../components/Drawer'
import { useUI } from '../store/ui'

const COLS = 'minmax(180px,1fr) 90px 130px 90px 90px 140px 150px'

export function Gateways() {
  const [rails, setRails] = useState<Gateway[] | null>(null)
  const [accounts, setAccounts] = useState<Account[]>([]) // for the action drawers' account picker
  const [err, setErr] = useState('')
  const [drawer, setDrawer] = useState<null | 'register' | 'stk' | 'test'>(null)

  async function load() {
    setErr('')
    try {
      const [gws, accts] = await Promise.all([listGateways(), listAccounts()])
      setRails(gws)
      setAccounts(accts)
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not load gateways')
    }
  }
  useEffect(() => {
    void load()
  }, [])

  return (
    <div className="page">
      <div className="ph">
        <div>
          <h1>Gateways</h1>
          <div className="desc">The M-Pesa rails this console transacts over, and their health. (Accounts are the specific paybills.)</div>
        </div>
        <Can perm="manage:config">
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn secondary" onClick={() => setDrawer('register')}>
              Register URLs
            </button>
            <button className="btn secondary" onClick={() => setDrawer('stk')}>
              STK push
            </button>
            <button className="btn primary" onClick={() => setDrawer('test')}>
              Test rail
            </button>
          </div>
        </Can>
      </div>

      {err && <div className="auth-error mt8">{err}</div>}

      <div className="tbl tbl-scroll">
        <div className="tr head" style={{ gridTemplateColumns: COLS }}>
          <div>Rail</div>
          <div>Direction</div>
          <div>Status</div>
          <div>Accounts</div>
          <div>Volume</div>
          <div>Delivered / Failed</div>
          <div>Last activity</div>
        </div>
        {rails === null &&
          [0, 1, 2, 3].map((i) => (
            <div className="tr body" style={{ gridTemplateColumns: COLS }} key={i}>
              {[0, 1, 2, 3, 4, 5, 6].map((j) => (
                <div key={j} className="skeleton" style={{ width: '60%' }} />
              ))}
            </div>
          ))}
        {rails?.map((g) => (
          <div className="tr body" style={{ gridTemplateColumns: COLS }} key={g.key}>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.name}</div>
            <div><StatusChip status={g.direction === 'in' ? 'In' : 'Out'} tone={g.direction === 'in' ? 'info' : 'neutral'} /></div>
            <div><StatusChip status={g.configured ? 'Active' : 'No account'} tone={g.configured ? undefined : 'danger'} /></div>
            <div className="num">{g.accounts}</div>
            <div className="num">{g.volume}</div>
            <div><span className="num">{g.delivered}</span><span className="muted"> / {g.failed}</span></div>
            <div className="muted" style={{ fontSize: 12 }}>{fmtDateTime(g.last_activity)}</div>
          </div>
        ))}
      </div>

      {drawer === 'register' && <RegisterDrawer accounts={accounts} onClose={() => setDrawer(null)} />}
      {drawer === 'stk' && <StkDrawer accounts={accounts} onClose={() => setDrawer(null)} />}
      {drawer === 'test' && <TestRailDrawer accounts={accounts} onClose={() => setDrawer(null)} />}
    </div>
  )
}

// AccountField picks an account by its unique id (value = a.id), showing the role so same-shortcode
// duplicates are distinguishable — the account the operator picks is the one that transacts.
function AccountField({ accounts, value, onChange }: { accounts: Account[]; value: string; onChange: (v: string) => void }) {
  return (
    <div className="field">
      <label>Account</label>
      <select className="select" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Select an account…</option>
        {accounts.map((a) => (
          <option key={a.id} value={a.id}>
            {a.name} · {a.number} ({a.role})
          </option>
        ))}
      </select>
    </div>
  )
}

function RegisterDrawer({ accounts, onClose }: { accounts: Account[]; onClose: () => void }) {
  const [accountId, setAccountId] = useState('')
  const [baseUrl, setBaseUrl] = useState('')
  const [busy, setBusy] = useState(false)
  const showToast = useUI((s) => s.showToast)
  async function submit() {
    setBusy(true)
    try {
      await registerUrls({ account_id: accountId, base_url: baseUrl })
      showToast('Callback URLs registered with Safaricom', 'success')
      onClose()
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Register failed', 'danger')
    } finally {
      setBusy(false)
    }
  }
  return (
    <DrawerShell
      title="Register callback URLs"
      onClose={onClose}
      foot={
        <>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={busy || !accountId || !baseUrl} onClick={() => void submit()}>
            {busy ? 'Registering…' : 'Register'}
          </button>
        </>
      }
    >
      <AccountField accounts={accounts} value={accountId} onChange={setAccountId} />
      <div className="field">
        <label>Public base URL</label>
        <input className="input" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://abc123.ngrok.io" />
      </div>
      <div className="muted" style={{ fontSize: 12 }}>Safaricom will post C2B validation/confirmation to this base. Use your tunnel or prod URL.</div>
    </DrawerShell>
  )
}

function StkDrawer({ accounts, onClose }: { accounts: Account[]; onClose: () => void }) {
  const [accountId, setAccountId] = useState('')
  const [phone, setPhone] = useState('')
  const [amount, setAmount] = useState('')
  const [reference, setReference] = useState('')
  const [busy, setBusy] = useState(false)
  const showToast = useUI((s) => s.showToast)
  async function submit() {
    setBusy(true)
    try {
      const res = await stkPush({ account_id: accountId, phone, amount, reference })
      showToast(res.checkout_request_id ? `STK prompt sent (${res.checkout_request_id})` : 'STK prompt sent', 'success')
      onClose()
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'STK push failed', 'danger')
    } finally {
      setBusy(false)
    }
  }
  return (
    <DrawerShell
      title="Trigger STK push"
      onClose={onClose}
      foot={
        <>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={busy || !accountId || !phone || !amount} onClick={() => void submit()}>
            {busy ? 'Sending…' : 'Send prompt'}
          </button>
        </>
      }
    >
      <AccountField accounts={accounts} value={accountId} onChange={setAccountId} />
      <div className="field">
        <label>Customer phone</label>
        <input className="input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="2547XXXXXXXX" />
      </div>
      <div className="field">
        <label>Amount (KES)</label>
        <input className="input" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="100" />
      </div>
      <div className="field">
        <label>Reference (optional)</label>
        <input className="input" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Order 42" />
      </div>
    </DrawerShell>
  )
}

type Rail = 'b2c' | 'pochi' | 'buygoods' | 'paybill' | 'c2b'
const RAILS: { key: Rail; label: string }[] = [
  { key: 'b2c', label: 'B2C — pay a phone' },
  { key: 'pochi', label: 'Pochi — pay a phone' },
  { key: 'buygoods', label: 'B2B Buy Goods — pay a till' },
  { key: 'paybill', label: 'B2B Paybill — pay a paybill' },
  { key: 'c2b', label: 'C2B — simulate a collection' },
]

// TestRailDrawer fires a small FIXED-amount test on a chosen rail (money-out = KES 10, C2B = KES 1). The amount is
// baked in as a guard-rail; idempotency keys are generated per submit. Money-out submits then forces a dispatch
// (the background dispatcher may be off). Async results appear under Transactions → Outflows / Inflows.
function TestRailDrawer({ accounts, onClose }: { accounts: Account[]; onClose: () => void }) {
  const [rail, setRail] = useState<Rail>('b2c')
  const [accountId, setAccountId] = useState('')
  const [phone, setPhone] = useState('')
  const [recv, setRecv] = useState('') // receiving till / paybill
  const [account, setAccount] = useState('') // account-at-paybill
  const [billRef, setBillRef] = useState('')
  const [busy, setBusy] = useState(false)
  const showToast = useUI((s) => s.showToast)

  const isC2B = rail === 'c2b'
  const isB2B = rail === 'buygoods' || rail === 'paybill'
  const amount = isC2B ? '1' : '10'
  const ready = !!accountId && (isB2B ? !!recv && !!phone && (rail === 'paybill' ? !!account : true) : !!phone)

  async function submit() {
    setBusy(true)
    try {
      const idem = `test-${rail}-${crypto.randomUUID()}`
      if (rail === 'c2b') {
        await simulateC2B({ account_id: accountId, phone, bill_ref: billRef || 'TEST' })
        showToast('C2B collection simulated (KES 1) — result under Transactions → Inflows', 'success')
      } else {
        const common = { account_id: accountId, amount: '10', reference: idem, instruction_id: idem, remarks: 'console rail test' }
        if (rail === 'b2c') await submitB2C({ ...common, phone })
        else if (rail === 'pochi') await submitPochi({ ...common, phone })
        else await submitB2B({ ...common, type: rail, receiving_short_code: recv, account_number: rail === 'paybill' ? account : undefined, requester_phone: phone })
        await dispatchPayouts() // force the send now (RUN_JOBS may be off)
        showToast(`${rail.toUpperCase()} test sent (KES 10) — result under Transactions → Outflows`, 'success')
      }
      onClose()
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Rail test failed', 'danger')
    } finally {
      setBusy(false)
    }
  }

  return (
    <DrawerShell
      title="Test a payment rail"
      onClose={onClose}
      foot={
        <>
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={busy || !ready} onClick={() => void submit()}>
            {busy ? 'Sending…' : `Send test (KES ${amount})`}
          </button>
        </>
      }
    >
      <div className="field">
        <label>Rail</label>
        <select className="select" value={rail} onChange={(e) => setRail(e.target.value as Rail)}>
          {RAILS.map((r) => (
            <option key={r.key} value={r.key}>{r.label}</option>
          ))}
        </select>
      </div>
      <AccountField accounts={accounts} value={accountId} onChange={setAccountId} />
      {isB2B && (
        <div className="field">
          <label>{rail === 'paybill' ? 'Recipient paybill' : 'Recipient till'}</label>
          <input className="input" value={recv} onChange={(e) => setRecv(e.target.value)} placeholder="e.g. 600000" />
        </div>
      )}
      {rail === 'paybill' && (
        <div className="field">
          <label>Account number (at paybill)</label>
          <input className="input" value={account} onChange={(e) => setAccount(e.target.value)} placeholder="e.g. INV-123" />
        </div>
      )}
      <div className="field">
        <label>{isC2B ? 'Payer phone' : isB2B ? 'Requester phone' : 'Recipient phone'}</label>
        <input className="input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="254708374149" />
      </div>
      {isC2B && (
        <div className="field">
          <label>Bill reference (optional)</label>
          <input className="input" value={billRef} onChange={(e) => setBillRef(e.target.value)} placeholder="Order 42" />
        </div>
      )}
      <div className="field">
        <label>Amount (fixed test)</label>
        <input className="input" value={`KES ${amount}`} readOnly disabled />
      </div>
      <div className="muted" style={{ fontSize: 12 }}>
        {isC2B
          ? 'Simulates an inbound customer payment (sandbox only). Result appears under Transactions → Inflows.'
          : 'Fires a KES 10 payout and dispatches it now. Result appears under Transactions → Outflows.'}
      </div>
    </DrawerShell>
  )
}
